/**
 * views/reporting/useReportingSections.js - the figures behind the KPI
 * row, bid history, pipeline and win rate by customer, revenue trend, the
 * Viz Studio datasets and the status donut.
 * Moved verbatim out of ReportingView.jsx; the view passes in the values
 * it reads.
 */
import { useMemo } from 'react';
import { quoteYear, quoteDateLocal } from '../../context/AppContext';
import { safeSum, trunc, getISOWeek } from './reportingHelpers';
import { getFilteredSets, filterByDate } from './chartFilters';

export function useReportingSections({
  projectsThisYear,
  allEngineers,
  validProjects,
  bidHistFilter,
  bidHistCustomFrom,
  bidHistCustomTo,
  allProjects,
  valByField,
  revFilter,
  revCustomFrom,
  revCustomTo,
  countByField,
}) {
  // -- FILTERED DATA PER SECTION -------------------------

  // KPI section (uses global year filter)
  const kpiSets = useMemo(() => getFilteredSets(projectsThisYear), [projectsThisYear]);
  // Open-only sum on the raw status. Since 9/29/26 Open also holds the bids
  // that used to be Budget. GlobalOps is the source of truth since 10/1/26
  // (docs/ARCHITECTURE.md section 4).
  const kpiOpenValue = safeSum(kpiSets.open);
  const kpiWonValue = safeSum(kpiSets.won);
  const kpiLostValue = safeSum(kpiSets.lost);
  const kpiTotalClosed = kpiSets.won.length + kpiSets.lost.length;
  const kpiWinRate = kpiTotalClosed > 0 ? ((kpiSets.won.length / kpiTotalClosed) * 100).toFixed(2) : 0;
  // Win margin: (won sell - won cost) / won sell, where cost is the quoted
  // COGS - served override-aware as totalQuotedCogs.
  const kpiWonCogs = kpiSets.won.reduce((s, p) => s + (parseFloat(p.totalQuotedCogs ?? p.total_quoted_cogs) || 0), 0);
  const kpiWinMargin = kpiWonValue > 0 ? ((kpiWonValue - kpiWonCogs) / kpiWonValue) * 100 : null;
  const engOnSite = allEngineers.filter((e) => e.status === 'On-Site').length;
  const engOffice = allEngineers.filter((e) => e.status === 'Office').length;
  const engPTO = allEngineers.filter((e) => e.status === 'PTO').length;

  // Bid History section
  const bidHistProjects = useMemo(
    () => filterByDate(validProjects, bidHistFilter, bidHistCustomFrom, bidHistCustomTo),
    [allProjects, bidHistFilter, bidHistCustomFrom, bidHistCustomTo]
  );
  const bidsByYear = useMemo(() => {
    const m = {};
    bidHistProjects.forEach((p) => {
      const d = p.quoteDate || p.quote_date;
      if (!d) return;
      const y = quoteYear(d);
      if (y < 2021) return;
      if (!m[y]) m[y] = { year: String(y), Won: 0, Lost: 0, Open: 0 };
      const s = (p.status || '').toLowerCase();
      if (s === 'won') m[y].Won++;
      else if (s === 'lost') m[y].Lost++;
      else if (s === 'open') m[y].Open++;
    });
    return Object.values(m).sort((a, b) => a.year.localeCompare(b.year));
  }, [bidHistProjects]);
  const avgWinsPerYear = bidsByYear.length > 0 ? Math.round(bidsByYear.reduce((s, y) => s + y.Won, 0) / bidsByYear.length) : 0;

  // Pipeline by Customer section (uses global year filter)
  const pipelineSets = useMemo(() => getFilteredSets(projectsThisYear), [projectsThisYear]);
  const pipelineByCustomer = useMemo(() => valByField(pipelineSets.activeBids, 'customer', 8), [pipelineSets.activeBids]);

  // Win Rate by Customer section (uses global year filter)
  const winRateByCustomer = useMemo(() => {
    const custMap = {};
    projectsThisYear.forEach((p) => {
      const c = p.customer;
      if (!c) return;
      const s = (p.status || '').toLowerCase();
      if (s !== 'won' && s !== 'lost') return;
      if (!custMap[c]) custMap[c] = { won: 0, lost: 0 };
      if (s === 'won') custMap[c].won++;
      else custMap[c].lost++;
    });
    return Object.entries(custMap)
      .filter(([, v]) => v.won + v.lost >= 1)
      .map(([name, v]) => ({
        name: trunc(name.replace('FANUC America - ', 'FANUC ')),
        won: v.won,
        lost: v.lost,
        winRate: parseFloat(((v.won / (v.won + v.lost)) * 100).toFixed(1)),
      }))
      .sort((a, b) => b.winRate - a.winRate);
  }, [projectsThisYear]);

  // Revenue Trend section
  const revProjects = useMemo(
    () => filterByDate(validProjects, revFilter, revCustomFrom, revCustomTo),
    [allProjects, revFilter, revCustomFrom, revCustomTo]
  );
  const revTrendData = useMemo(() => {
    // Determine grouping mode based on filter
    const groupByYear = revFilter === 'byyear' || revFilter === 'lastyear';
    const groupByWeek = revFilter === '1w' || revFilter === '2w';

    if (groupByYear) {
      const m = {};
      revProjects.forEach((p) => {
        const d = p.quoteDate || p.quote_date;
        if (!d) return;
        const y = quoteYear(d);
        if (y < 2021) return;
        const v = parseFloat(p.quotedPrice || p.quoted_price || 0);
        if (!isFinite(v) || v >= 500000000) return;
        if (!m[y]) m[y] = { period: String(y), wonValue: 0, openValue: 0 };
        const s = (p.status || '').toLowerCase();
        if (s === 'won') m[y].wonValue += v;
        else if (s === 'open') m[y].openValue += v;
      });
      return Object.values(m).sort((a, b) => a.period.localeCompare(b.period));
    }
    if (groupByWeek) {
      const m = {};
      revProjects.forEach((p) => {
        const d = p.quoteDate || p.quote_date;
        if (!d) return;
        const dt = quoteDateLocal(d);
        const v = parseFloat(p.quotedPrice || p.quoted_price || 0);
        if (!isFinite(v) || v >= 500000000) return;
        const wk = getISOWeek(dt);
        const yr = dt.getFullYear();
        const key = `${yr}-W${String(wk).padStart(2, '0')}`;
        if (!m[key]) m[key] = { period: key, wonValue: 0, openValue: 0 };
        const s = (p.status || '').toLowerCase();
        if (s === 'won') m[key].wonValue += v;
        else if (s === 'open') m[key].openValue += v;
      });
      return Object.values(m).sort((a, b) => a.period.localeCompare(b.period));
    }
    // monthly grouping
    const m = {};
    revProjects.forEach((p) => {
      const d = p.quoteDate || p.quote_date;
      if (!d) return;
      const dt = quoteDateLocal(d);
      const v = parseFloat(p.quotedPrice || p.quoted_price || 0);
      if (!isFinite(v) || v >= 500000000) return;
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      if (!m[key]) m[key] = { period: key, wonValue: 0, openValue: 0 };
      const s = (p.status || '').toLowerCase();
      if (s === 'won') m[key].wonValue += v;
      else if (s === 'open') m[key].openValue += v;
    });
    return Object.values(m).sort((a, b) => a.period.localeCompare(b.period));
  }, [revProjects, revFilter]);

  // Viz Studio section
  // Viz Studio (uses global year filter)
  const vizProjects = projectsThisYear;
  const vizSets = useMemo(() => getFilteredSets(vizProjects), [vizProjects]);
  const vizDatasets = useMemo(() => {
    const vizWonValue = safeSum(vizSets.won);
    return {
      pipeline_cust: valByField(vizSets.activeBids, 'customer', 8),
      won_rev_cust: valByField(vizSets.won, 'customer', 8),
      total_val_cust: valByField(vizProjects, 'customer', 8),
      value_stage: [
        { name: 'Open Bids', value: safeSum(vizSets.open) },
        { name: 'Won', value: vizWonValue },
      ].filter((d) => d.value > 0),
      bids_cust: countByField(vizProjects, 'customer', 10),
      bids_status: [
        { name: 'Won', value: vizSets.won.length },
        { name: 'Open', value: vizSets.open.length },
        { name: 'Lost', value: vizSets.lost.length },
        { name: 'Cancelled', value: vizSets.cancelled.length },
      ].filter((d) => d.value > 0),
      wr_cust: (() => {
        const cm = {};
        vizProjects.forEach((p) => {
          const c = p.customer;
          if (!c) return;
          const s = (p.status || '').toLowerCase();
          if (s !== 'won' && s !== 'lost') return;
          if (!cm[c]) cm[c] = { won: 0, lost: 0 };
          if (s === 'won') cm[c].won++;
          else cm[c].lost++;
        });
        return Object.entries(cm)
          .filter(([, v]) => v.won + v.lost >= 1)
          .map(([name, v]) => ({ name: trunc(name), value: parseFloat(((v.won / (v.won + v.lost)) * 100).toFixed(1)) }))
          .sort((a, b) => b.value - a.value);
      })(),
      by_type: countByField(vizProjects, 'type', 8),
      by_division: countByField(vizProjects, 'division', 8),
      by_billing: countByField(vizProjects, 'billingType', 8),
      by_engineer: countByField(vizProjects, 'engineer', 10),
      by_manager: countByField(vizProjects, 'approvalManager', 10),
      eng_status: [
        { name: 'On-Site', value: engOnSite },
        { name: 'Office', value: engOffice },
        { name: 'PTO', value: engPTO },
      ].filter((d) => d.value > 0),
      eng_capacity: allEngineers
        .filter((e) => e.status !== 'PTO' && parseInt(e.hours || 0) > 0)
        .slice(0, 10)
        .map((e) => ({ name: `${(e.name || '').charAt(0)}. ${(e.name || '').split(' ').pop()}`, value: parseInt(e.hours || 0) })),
    };
  }, [vizProjects, vizSets, engOnSite, engOffice, engPTO, allEngineers]);

  // Status Donut section
  // Status Donut (uses global year filter)
  const donutSets = useMemo(() => getFilteredSets(projectsThisYear), [projectsThisYear]);
  // No Quote (Paul Q5: we backed out) is not in getFilteredSets; counted here so the total matches the table
  const donutNoQuote = useMemo(
    () => projectsThisYear.filter((p) => (p.status || '').toLowerCase() === 'no quote').length,
    [projectsThisYear]
  );
  const statusDonut = [
    { name: 'Won', value: donutSets.won.length, color: '#1B5E20' },
    { name: 'Open', value: donutSets.open.length, color: '#1565C0' },
    { name: 'Lost', value: donutSets.lost.length, color: '#B71C1C' },
    { name: 'Cancelled', value: donutSets.cancelled.length, color: '#757575' },
    { name: 'No Quote', value: donutNoQuote, color: '#7A6A5B' },
  ].filter((d) => d.value > 0);
  const statusTotal = statusDonut.reduce((s, d) => s + d.value, 0);

  // Utilization now comes from the Manpower CALENDAR, not the utilization
  // workbook: the workbook was an export somebody had to remember to refresh,
  // the calendar is what the team keeps current. Five months (two back, this
  // one, two forward), PTO and weekends excluded from both sides, NA roster
  // only, interns not counted.
  // See src/utils/utilizationFromCalendar.js.
  return {
    kpiOpenValue,
    kpiSets,
    kpiWonValue,
    kpiLostValue,
    kpiWinMargin,
    kpiWonCogs,
    kpiWinRate,
    bidsByYear,
    avgWinsPerYear,
    pipelineByCustomer,
    winRateByCustomer,
    revTrendData,
    vizDatasets,
    statusTotal,
    statusDonut,
  };
}
